
export interface User {
  id: string;
  name: string;
  email: string;
  avatar?: string;
  backgroundUrl?: string;
  isPro: boolean;
  joinDate: string;
  lang: Language;
  role?: 'user' | 'admin';
  points?: number;
}

export interface PointLog {
  id: string;
  user_id: string;
  amount: number;
  reason: string;
  created_at: string;
}

export interface Notice {
  id: string;
  title_ko: string;
  title_en: string;
  // title_ja?: string;
  // title_es?: string;
  // title_fr?: string;
  content_ko: string;
  content_en: string;
  // content_ja?: string;
  // content_es?: string;
  // content_fr?: string;
  description_ko?: string;
  description_en?: string;
  // description_ja?: string;
  // description_es?: string;
  // description_fr?: string;
  banner: string | null;
  is_published: boolean;
  start_at: string | null;
  end_at: string | null;
  created_by: string;
  user_id: string;
  created_at: string;
  updated_at: string;
}


export interface Player {
  id: string;
  name: string;
  shortName: string;
  position: string;
  team: string;
  currentTeamId?: string;
  teamLogo: string;
  avatar: string;
  marketValue: string;
  age?: string;
  nationality?: string;
  height?: string;
  weight?: string;
  foot?: string;
  shirtNumber?: string;
  socialMedia?: { platform: 'instagram' | 'twitter' | 'facebook' | 'tiktok'; handle: string; url: string }[];
  stats: {
    year: number;
    goals: number;
    assists: number;
    appearances: number;
    minutesPlayed: number;
    rating: number;
  }[];
  detailedStats?: {
    season: string;
    competition: string;
    competitionId?: string;
    competitionLogo: string;
    team: string;
    teamId?: string;
    goals: number;
    assists: number;
    appearances: number;
    minutesPlayed?: number;
    rating: number;
  }[];
  history: {
    seasons: string;
    club: string;
    clubId?: string;
    clubLogo: string;
    fee: string;
    type: 'transfer' | 'loan' | 'free';
  }[];
}

export interface NewsItem {
  id: string;
  title: string;
  summary: string;
  content?: string;
  audio_url?: string;
  time: string;
  publishedAt?: string;
  type?: string;
  source?: string;
  image?: string;
  background?: string;
  videoUrl?: string;
  url?: string;
  sourceName?: string;
  sourceIcon?: string;
  players?: string[];
  teams?: string[];
  user_id?: string;
  author?: { name: string; avatar: string };
  is_reported?: boolean;
  is_hidden?: boolean;
  lang?: string;
  views?: number;
  likes?: number;
  comments?: number;
}

export interface VideoItem {
  id: string;
  title: string;
  channel: string;
  videoId: string;
  description: string;
  channelId?: string;
  channelImage?: string;
  channel_url?: string;
  is_hidden?: boolean;
}

export interface HighlightItem {
    title: string;
    competition: string;
    league_id?: string;
    home_team?: string;
    home_team_id?: string;
    away_team?: string;
    away_team_id?: string;
    matchviewUrl: string;
    thumbnail: string;
    date: string;
    embed_id?: string;
    match_id?: string;
    videos: { title: string; embed: string }[];
}

export interface Game {
  id: string;
  league: string;
  leagueId?: string;
  leagueName?: string;
  leagueLogo?: string;
  homeTeam: { name: string; logo: string; id?: string };
  awayTeam: { name: string; logo: string; id?: string };
  time: string;
  dateLabel: string;
  status: 'Scheduled' | 'Live' | 'Finished' | 'Replay';
  score?: string;
  round?: string;
  currentMinute?: string;
  elapsed?: number;
  statusShort?: string; // 'FT' | 'AET' | 'PEN'
}

export interface StoreGame {
  id: string;
  title: string;
  category: string | null;
  icon: string | null;
  isNew: boolean;
  link: string | null;
  is_hidden?: boolean;
}

export interface GameScore {
  id?: string;
  user_id: string;
  game_id: string;
  score: number;
  played_at?: string;
  year?: number;
  week?: number;
}

export interface TotoRound {
  id: string;
  round_number: number;
  title_ko: string;
  title_en?: string;
  // title_ja?: string;
  // title_es?: string;
  // title_fr?: string;
  start_at: string;
  end_at: string;
  status: 'open' | 'closed' | 'finished';
  created_at?: string;
}

export interface TotoMatch {
  id: string;
  round_id: string;
  match_id: string; // football-api fixture id
  order_index: number;
  home_team_name: string;
  home_team_logo: string;
  away_team_name: string;
  away_team_logo: string;
  match_time: string;
  status: string;
  result?: '1' | 'X' | '2'; // 1: 승, X: 무, 2: 패
  home_team_id?: string;
  away_team_id?: string;
  league_id?: string;
}

export interface TotoPrediction {
  id: string;
  user_id: string;
  round_id: string;
  predictions: Record<string, ('1' | 'X' | '2')[]>; // match_id -> choices
  is_rewarded: boolean;
  correct_count: number;
  created_at?: string;
}

export interface StandingItem {
  rank: number;
  team: { id: string; name: string; logo: string };
  played: number;
  won: number;
  drawn: number;
  lost: number;
  goalsFor: number;
  goalsAgainst: number;
  goalDifference: number;
  points: number;
  form?: string[];
  group?: string;
}

export interface MatchStat {
    label: string;
    homeValue: number | string;
    awayValue: number | string;
    type: 'number' | 'percent';
}

export interface MatchEvent {
    id: string;
    minute: number;
    type: 'goal' | 'card' | 'sub';
    team: 'home' | 'away';
    player: string;
    player_id?: string;
    assist?: string;
    assist_id?: string;
    detail?: string;
}

export interface TeamColors {
    primary: string;
    number: string;
    border: string;
}

export interface TeamLineup {
    formation: string;
    startXI: { id: string; name: string; number: string; pos: string; grid: string | null; photo: string; captain?: boolean; rating?: string }[];
    substitutes: { id: string; name: string; number: string; pos: string; photo: string; captain?: boolean; rating?: string }[];
    coach: { id?: string; name: string; photo: string };
    colors?: {
        player: TeamColors;
        goalkeeper: TeamColors;
    };
}

export interface MatchInjury {
    player: { id: number; name: string; photo: string; type?: string; reason?: string };
    team: { id: number; name: string; logo: string };
    fixture: { id: number };
    injury?: { type: string; reason: string };
}

export interface MatchDetail extends Game {
venue?: string;
    venueCity?: string;
    venueId?: number;
    referee?: string;
    attendance?: string;
    stats: MatchStat[];
    events: MatchEvent[];
    goals?: { home: number | null, away: number | null };
    lineups?: {
        home: TeamLineup;
        away: TeamLineup;
    };
    injuries?: MatchInjury[];
    scoreDetails?: {
        halftime: { home: number | null, away: number | null };
        fulltime: { home: number | null, away: number | null };
        extratime: { home: number | null, away: number | null };
        penalty: { home: number | null, away: number | null };
    };
}

export interface Comment {
  id: string | number;
  post_id: string;
  user_id: string;
  content: string;
  image_url?: string;
  parent_id: string | number | null;
  created_at: string;
  likes_count: number;
  is_reported: boolean;
  username?: string;
  lang?: string;
  home_team_id?: string;
  away_team_id?: string;
  home_team_name?: string;
  away_team_name?: string;
  home_team_logo?: string;
  away_team_logo?: string;
}

export interface CommentWithUser extends Comment {
  user?: {
    name: string;
    avatar_url: string;
    favorite_team?: {
      name: string;
      logo: string;
    } | null;
  };
}

export interface StadiumPost {
  id: string;
  user_id: string;
  team_id: string | null;
  content: string;
  image_url: string | null;
  created_at: string;
  likes_count: number;
  comments_count: number;
  user?: {
    name: string;
    avatar_url: string;
  };
  team_info?: any;
  is_liked?: boolean;
  is_hidden?: boolean;
}

export interface MatchPrediction {
  id: string;
  post_id: string;
  user_id: string;
  predicted_winner: string;
  predicted_score: string;
  analysis_reason: string;
  analysis_reason_ko: string;
  // analysis_reason_ja?: string;
  // analysis_reason_es?: string;
  // analysis_reason_fr?: string;
  created_at: string;
  updated_at: string;
  win_probability?: number;
  home_team_id?: string;
  away_team_id?: string;
  matchdate?: string;
  league_id?: string;
  type?: string;
  // UI용 가공 필드
  user?: {
    name: string;
    avatar_url: string;
  };
  match_info?: any;
  views_count?: number;
  likes_count?: number;
  comments_count?: number;
}

export interface AnalysisPost {
  id: string;
  user_id: string;
  match_id: string | null;
  title: string;
  content: string;
  image_url: string | null;
  created_at: string;
  views_count: number;
  likes_count: number;
  comments_count: number;
  user?: {
    name: string;
    avatar_url: string;
  };
  match_info?: any;
}

export enum Tab {
  HOME = 'HOME',
  NEWS = 'NEWS',
  NEWS_POST = 'NEWS_POST',
  NEWS_EDIT = 'NEWS_EDIT',
  PLAYER = 'PLAYER',
  GAMES = 'GAMES',
  GAME_DETAIL = 'GAME_DETAIL',
  SHORTS = 'SHORTS',
  SETTINGS = 'SETTINGS',
  LOGIN = 'LOGIN',
  SIGNUP = 'SIGNUP',
  MYPAGE = 'MYPAGE',
  ADMIN = 'ADMIN',
  CATEGORY_LIST = 'CATEGORY_LIST',
  HELP = 'HELP',
  PRIVACY = 'PRIVACY',
  API_INFO = 'API_INFO',
  CHANGE_PASSWORD = 'CHANGE_PASSWORD',
  HIGHLIGHTS = 'HIGHLIGHTS',
  ANALYSIS = 'ANALYSIS',
  MAGAZINE_LIST = 'MAGAZINE_LIST',
  WEBTOON_LIST = 'WEBTOON_LIST',
  COMMUNITY = 'COMMUNITY',
  GAME_STORE = 'GAME_STORE',
  GAME_NEON_PITCH = 'GAME_NEON_PITCH',
  GAME_PENALTY_SHOOTOUT = 'GAME_PENALTY_SHOOTOUT',
  GAME_FOOTY_MASTER = 'GAME_FOOTY_MASTER',
  GAME_TOTO = 'GAME_TOTO',
  GAME_TOTO_RESULT = 'GAME_TOTO_RESULT'
}

// export type Language = 'en' | 'ko' | 'ja' | 'es' | 'fr';
export type Language = 'en' | 'ko';

export interface RouteState {
  tab: Tab;
  id?: string;
  date?: string;
  subType?: string;
}

export interface LeagueDB {
  id: string;
  name: string;
  logo_url: string;
  country?: string;
  order?: number;
}

export interface PlayerSearchResult {
  id: string;
  name: string;
  team: string;
  avatar: string;
  position?: string;
  nationality?: string;
}

export interface TransferNews {
  id: string;
  playerId: string;
  player: {
    name: string;
    avatar: string;
    prevClub: string;
    prevClubLogo: string;
  };
  toClub: {
    name: string;
    logo: string;
  };
  fee: string;
  marketValue: string;
  date: string;
  status: string;
    league: string;
    views?: number;
    likes?: number;
    comments?: number;
}

export interface TransferGroup {
  id: string | number;
  season: string;
  type?: string;
  window?: string;
}


export interface Page {
  id: string;
  title: string;
  content: string;
  coverImage?: string;
  tags: string[];
  createdAt: number;
}

export interface ActiveVideo {
  videoId: string;
  title: string;
  matchId?: string;
  currentTime?: number;
  isPaused?: boolean;
}

export interface TeamSelection {
  id: string;
  user_id: string;
  team_id: string;
  is_favorite: boolean;
  created_at?: string;
}

export interface QuizQuestionDB {
    id: string;
    category: string;
    question_ko: string;
    question_en?: string;
    // question_ja?: string;
    // question_es?: string;
    // question_fr?: string;
    options_ko: string[];
    options_en?: string[];
    // options_ja?: string[];
    // options_es?: string[];
    // options_fr?: string[];
    correct_answer: number;
    explanation_ko?: string;
    explanation_en?: string;
    // explanation_ja?: string;
    // explanation_es?: string;
    // explanation_fr?: string;
    difficulty: string;
    created_at?: string;
}
